import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/common";
import { Saved, StepShell } from "./StepShell";
import { AttemptsBox } from "./AttemptsBox";

const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const blank = (t) => ({
  call_date: t.customer_call_date || today(), summary: t.customer_call_summary || "", action_taken: t.customer_action_taken || "",
  action_type: t.customer_action_type || "", amount: t.customer_action_amount ?? "",
});

const ActionPicker = ({ actions, value, onChange }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup">
    {actions.map((a) => (
      <button key={a} type="button" role="radio" aria-checked={value === a} onClick={() => onChange(a)} data-testid={`action-type-${a.toLowerCase().replace(/\s+/g, "-")}`}
        className={`h-10 rounded-full border px-4 text-sm font-medium transition-colors ${value === a ? "border-indigo-600 bg-indigo-600 text-white ring-2 ring-indigo-200" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
        {a}
      </button>
    ))}
  </div>
);

export const CustomerStep = ({ t, meta, canEdit, onSaved }) => {
  const done = !!t.customer_call_date;
  const closed = ["Resolved", "Closed"].includes(t.status);
  const locked = !t.outlet_call_date;
  const [editing, setEditing] = useState(!done);
  const [f, setF] = useState(blank(t));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    setF(blank(t));
    setEditing(!t.customer_call_date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id, t.customer_call_date, t.customer_call_summary]);

  const needAmount = f.action_type && f.action_type !== "No Action";
  const valid = f.call_date && f.summary.trim() && f.action_type && (!needAmount || Number(f.amount) > 0);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/followups/${t.id}/customer`, { ...f, amount: needAmount ? Number(f.amount) : null });
      toast.success("Customer call saved. Ticket closed");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const state = locked ? "locked" : done ? "done" : "active";
  const comp = [t.customer_action_type, t.customer_action_amount ? `₹${Number(t.customer_action_amount).toLocaleString("en-IN")}` : ""].filter(Boolean).join(" · ");
  const edit = canEdit && !closed && done && !editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="customer-step-edit">Edit</Button>;
  return (
    <StepShell n={2} testid="step-customer" state={state} title="Call the customer" right={edit}
      hint={locked ? "Unlocks after the outlet call is saved." : done ? `Called on ${fmtDate(t.customer_call_date)}` : "Explain what happened, then offer a coupon, discount or refund if needed."}>
      {locked ? null : editing && canEdit && !closed ? (
        <div className="space-y-4">
          {!done && <AttemptsBox t={t} max={meta.max_attempts} onSaved={onSaved} />}
          <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="cc-date">Date of call</Label>
              <Input id="cc-date" type="date" max={today()} data-testid="customer-call-date-input" value={f.call_date} onChange={(e) => set("call_date")(e.target.value)} className="h-10 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cc-summary">What did the customer say?</Label>
              <Textarea id="cc-summary" rows={3} data-testid="customer-summary-input" value={f.summary} onChange={(e) => set("summary")(e.target.value)} placeholder="Summary of the conversation" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-action">Action taken</Label>
            <Textarea id="cc-action" rows={2} data-testid="customer-action-input" value={f.action_taken} onChange={(e) => set("action_taken")(e.target.value)} placeholder="e.g. Apologised and shared a coupon for the next order" />
          </div>
          <div className="space-y-2">
            <Label>Compensation</Label>
            <ActionPicker actions={meta.actions} value={f.action_type} onChange={set("action_type")} />
          </div>
          {needAmount && (
            <div className="max-w-[220px] space-y-1.5">
              <Label htmlFor="cc-amount">{f.action_type} amount (₹)</Label>
              <Input id="cc-amount" type="number" min="1" inputMode="decimal" data-testid="customer-amount-input" value={f.amount} onChange={(e) => set("amount")(e.target.value)} className="h-10 rounded-xl" />
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy || !valid} className="rounded-xl" data-testid="customer-step-save">{busy && <Spinner className="mr-2 h-4 w-4" />}Save customer call</Button>
            {done && <Button variant="ghost" onClick={() => setEditing(false)} data-testid="customer-step-cancel">Cancel</Button>}
          </div>
        </div>
      ) : done ? (
        <Saved items={[["Customer said", t.customer_call_summary], ["Action taken", t.customer_action_taken], ["Compensation", comp]]} />
      ) : t.call_attempts ? (
        <p className="text-sm text-slate-500">{t.call_attempts} unanswered call{t.call_attempts > 1 ? "s" : ""}.</p>
      ) : null}
    </StepShell>
  );
};
